import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { fromZonedTime } from 'date-fns-tz';

interface PauseTimeByCategoryProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface CategoryPauseStats {
  category: string;
  totalPauseMinutes: number;
  avgPausePerTicket: number;
  ticketCount: number;
  pausePercentage: number;
}

interface ReasonDrilldown {
  reason: string;
  totalMinutes: number;
  count: number;
}

export function PauseTimeByCategory({ shopId, startDate, endDate, shopTimezone }: PauseTimeByCategoryProps) {
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState<CategoryPauseStats[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [reasonDrilldown, setReasonDrilldown] = useState<ReasonDrilldown[]>([]);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);

  useEffect(() => {
    loadCategoryPauseStats();
    // KPI-R70-FIX (MED-1): shopTimezone dep — fromZonedTime inside would
    // otherwise use a stale TZ for UTC bounds after a shop switch.
  }, [shopId, startDate, endDate, shopTimezone]);

  async function loadCategoryPauseStats() {
    setLoading(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get pause sessions - only completed ones (with duration)
      // This naturally excludes active pauses on parking lot tickets
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('id, ticket_id, pause_reason, duration_minutes')
        .eq('shop_id', shopId)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null);

      if (!pauseSessions?.length) {
        setCategoryStats([]);
        setLoading(false);
        return;
      }

      // Get ticket job categories
      const ticketIds = [...new Set(pauseSessions.map(s => s.ticket_id))];
      const { data: ticketJobs } = await supabase
        .from('ticket_jobs')
        .select('ticket_id, job_id')
        .in('ticket_id', ticketIds);

      const jobIds = [...new Set(ticketJobs?.map(tj => tj.job_id) || [])];
      const { data: jobs } = await supabase
        .from('jobs_catalog')
        .select('id, category_key')
        .in('id', jobIds);

      // Map ticket to primary category (first job's category)
      const jobCategoryMap = new Map(jobs?.map(j => [j.id, j.category_key]) || []);
      const ticketCategoryMap = new Map<string, string>();
      
      ticketJobs?.forEach(tj => {
        if (!ticketCategoryMap.has(tj.ticket_id)) {
          const category = jobCategoryMap.get(tj.job_id);
          if (category) {
            ticketCategoryMap.set(tj.ticket_id, category);
          }
        }
      });

      // Get total actual time per category for percentage calculation
      const { data: completedTickets } = await supabase
        .from('tickets')
        .select('id, actual_minutes')
        .eq('shop_id', shopId)
        .eq('status', 'Completed')
        // CSI-S1: exclude soft-deleted Completed tickets from pause-by-category analytics.
        .is('deleted_at', null)
        .in('id', ticketIds);

      const ticketActualMap = new Map(completedTickets?.map(t => [t.id, t.actual_minutes || 0]) || []);

      // Aggregate by category
      const categoryMap = new Map<string, { pause: number; actual: number; tickets: Set<string> }>();
      
      pauseSessions.forEach(s => {
        const category = ticketCategoryMap.get(s.ticket_id) || 'Unknown';
        const existing = categoryMap.get(category) || { pause: 0, actual: 0, tickets: new Set() };
        existing.pause += s.duration_minutes || 0;
        existing.tickets.add(s.ticket_id);
        categoryMap.set(category, existing);
      });

      // Add actual minutes
      ticketCategoryMap.forEach((category, ticketId) => {
        const existing = categoryMap.get(category);
        if (existing && ticketActualMap.has(ticketId)) {
          existing.actual += ticketActualMap.get(ticketId) || 0;
        }
      });

      const stats: CategoryPauseStats[] = Array.from(categoryMap.entries())
        .map(([category, data]) => ({
          category,
          totalPauseMinutes: data.pause,
          avgPausePerTicket: data.tickets.size > 0 ? Math.round(data.pause / data.tickets.size) : 0,
          ticketCount: data.tickets.size,
          pausePercentage: data.actual > 0 ? Math.round((data.pause / (data.actual + data.pause)) * 100) : 0,
        }))
        .sort((a, b) => b.totalPauseMinutes - a.totalPauseMinutes || a.category.localeCompare(b.category));

      setCategoryStats(stats);
    } catch (error) {
      console.error('Error loading category pause stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadReasonDrilldown(category: string) {
    setLoadingDrilldown(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get job IDs for this category
      const { data: categoryJobs } = await supabase
        .from('jobs_catalog')
        .select('id')
        .eq('category_key', category);

      const jobIds = categoryJobs?.map(j => j.id) || [];

      // Get tickets with these jobs
      const { data: ticketJobs } = await supabase
        .from('ticket_jobs')
        .select('ticket_id')
        .in('job_id', jobIds);

      const ticketIds = [...new Set(ticketJobs?.map(tj => tj.ticket_id) || [])];

      // Get pause sessions for these tickets
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('pause_reason, duration_minutes')
        .eq('shop_id', shopId)
        .in('ticket_id', ticketIds)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null);

      // Aggregate by reason
      const reasonMap = new Map<string, { minutes: number; count: number }>();
      pauseSessions?.forEach(s => {
        const existing = reasonMap.get(s.pause_reason) || { minutes: 0, count: 0 };
        existing.minutes += s.duration_minutes || 0;
        existing.count += 1;
        reasonMap.set(s.pause_reason, existing);
      });

      const drilldown: ReasonDrilldown[] = Array.from(reasonMap.entries())
        .map(([reason, data]) => ({
          reason,
          totalMinutes: data.minutes,
          count: data.count,
        }))
        .sort((a, b) => b.totalMinutes - a.totalMinutes);

      setReasonDrilldown(drilldown);
    } catch (error) {
      console.error('Error loading reason drilldown:', error);
    } finally {
      setLoadingDrilldown(false);
    }
  }

  function handleCategoryClick(category: string) {
    if (expandedCategory === category) {
      setExpandedCategory(null);
      setReasonDrilldown([]);
    } else {
      setExpandedCategory(category);
      loadReasonDrilldown(category);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pause Time by Job Category</CardTitle>
        <CardDescription>
          Click a category to see pause reason breakdown
        </CardDescription>
      </CardHeader>
      <CardContent>
        {categoryStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No pause data in this date range</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Total Pause</TableHead>
                <TableHead className="text-right">Avg/Ticket</TableHead>
                <TableHead className="text-right">Tickets</TableHead>
                <TableHead className="text-right">% Paused</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoryStats.map((cat) => (
                <>
                  <TableRow 
                    key={cat.category}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleCategoryClick(cat.category)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expandedCategory === cat.category ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {cat.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(cat.totalPauseMinutes)}</TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(cat.avgPausePerTicket)}</TableCell>
                    <TableCell className="text-right">{cat.ticketCount}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={cat.pausePercentage > 20 ? 'destructive' : 'secondary'}>
                        {cat.pausePercentage}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {expandedCategory === cat.category && (
                    <TableRow>
                      <TableCell colSpan={5} className="bg-muted/30 p-4">
                        {loadingDrilldown ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : reasonDrilldown.length === 0 ? (
                          <p className="text-muted-foreground text-sm text-center">No breakdown available</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Pause Reason</TableHead>
                                <TableHead className="text-right">Total Time</TableHead>
                                <TableHead className="text-right">Count</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {reasonDrilldown.map((r) => (
                                <TableRow key={r.reason}>
                                  <TableCell>{r.reason}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(r.totalMinutes)}</TableCell>
                                  <TableCell className="text-right">{r.count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
